import Phaser from 'phaser';
import {
  CHAPTER_FORMATS,
  CHAPTER_ORDERS,
  CREATOR_BORDERS,
  MAX_CHAPTER_LEVELS,
  MAX_NAME,
  addChapterLevel,
  chapterLevelLabel,
  emptyChapterDef,
  findBorder,
  type ChapterDef,
  type CreatorProject,
  type PublishedRow,
} from '../../config/creator';
import { HUB_MENU_TRACK } from '../../config/levels';
import { accountClient } from '../../account/accountUi';
import { myChapters, myPublished, saveChapter } from '../../account/explore';
import { newProjectId } from '../../account/creatorStore';
import { creatorStoreNow } from '../creator/storeAccess';
import { ScrollList } from '../../ui/scrollList';
import { drawStarfield } from '../starfield';
import { getMusicService } from '../audio/MusicService';
import {
  CREATOR_ORANGE,
  CREATOR_PANEL,
  CREATOR_TEAL,
  creatorButton,
  creatorFlash,
  creatorHeader,
} from '../creator/creatorUi';
import { canvasTextInput } from '../creator/textInput';

const LIST_X = 70;
const LIST_Y = 190;
const LIST_W = 700;
const LIST_H = 360;
const ROW_H = 40;
const SWATCH = 44;

export const CHAPTER_EMPTY = 'Press + to add one of your published levels';
export const CHAPTER_NAME_EMPTY = 'Give your chapter a name';
export const CHAPTER_FULL = `A chapter holds ${MAX_CHAPTER_LEVELS} levels at most`;

/** One of the player's levels, as the + picker offers it. */
interface Pickable {
  name: string;
  /** Its code when it is live in Explore; null otherwise. */
  code: string | null;
}

/**
 * Building a chapter (spec 2026-11-03 §5). Along the top: + (add one of your
 * LIVE levels), − (remove the last one added), CLEAR, and the chapter's name.
 * Under them the levels in order, labelled as they will play; beside them the
 * LEVEL FORMAT and ORDER selectors; under them the BORDER colour. SAVE keeps
 * it on the account (publishing is the menu's PUBLISH, like a level's).
 */
export class CreatorChapterScene extends Phaser.Scene {
  private editId: string | null = null;
  private name = '';
  /** The chapter being built (read by tests). */
  def: ChapterDef = emptyChapterDef();
  private pickable: Pickable[] = [];
  private namesByCode = new Map<string, string>();
  private list?: ScrollList;
  private countText?: Phaser.GameObjects.Text;
  private formatText?: Phaser.GameObjects.Text;
  private orderText?: Phaser.GameObjects.Text;
  private swatches: Phaser.GameObjects.Rectangle[] = [];
  private picker: Phaser.GameObjects.GameObject[] = [];
  private pickerList?: ScrollList;
  private saving = false;

  constructor() {
    super({ key: 'CreatorChapterScene' });
  }

  init(data: { id?: string }): void {
    this.editId = data?.id ?? null;
    this.sys.settings.data = {};
  }

  create(): void {
    this.saving = false;
    this.name = '';
    this.def = emptyChapterDef();
    this.swatches = [];
    this.picker = [];
    drawStarfield(this, 1280, 720);
    creatorHeader(this, this.editId ? 'EDIT CHAPTER' : 'NEW CHAPTER', () =>
      this.scene.start('CreatorMenuScene', { tab: 'chapters' }),
    );
    const music = getMusicService(this);
    music.playTrack(HUB_MENU_TRACK);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      music.stop();
      this.list?.destroy();
      this.pickerList?.destroy();
      this.list = undefined;
      this.pickerList = undefined;
    });
    void this.loadData();
  }

  private bail(message: string): void {
    creatorFlash(this, message, 360, '#ffcf6b');
    this.time.delayedCall(1600, () => this.scene.start('CreatorMenuScene', { tab: 'chapters' }));
  }

  private async loadData(): Promise<void> {
    const client = accountClient();
    if (!client) {
      this.bail('Sign in to make chapters');
      return;
    }
    let projects: CreatorProject[];
    try {
      projects = await (await creatorStoreNow()).list();
    } catch (e) {
      if (this.sys.isActive()) this.bail(e instanceof Error ? e.message : 'Try again');
      return;
    }
    const [pub, chapters] = await Promise.all([myPublished(client), myChapters(client)]);
    if (!this.sys.isActive()) return;
    if (!pub.ok || !chapters.ok) {
      this.bail("Couldn't load your levels — check your connection");
      return;
    }
    const live = new Map<string, PublishedRow>();
    for (const r of pub.value) if (r.live && !r.banned) live.set(r.sourceId, r);
    this.pickable = projects.map((p) => ({ name: p.name, code: live.get(p.id)?.code ?? null }));
    this.namesByCode = new Map(
      this.pickable.flatMap((p) => (p.code ? [[p.code, p.name] as [string, string]] : [])),
    );
    if (this.editId) {
      const existing = chapters.value.find((c) => c.id === this.editId);
      if (!existing) {
        this.bail("Couldn't find that chapter");
        return;
      }
      this.name = existing.name;
      this.def = {
        levels: existing.levels,
        border: existing.border,
        format: existing.format,
        order: existing.order,
      };
    }
    this.build();
  }

  private build(): void {
    // ── Top row: + − CLEAR, then the name ──
    creatorButton(
      this,
      110,
      126,
      '+',
      { name: 'chapter-add', width: 64, height: 64, fontSize: 40, fill: 0x2e9e4f },
      () => this.openPicker(),
    );
    creatorButton(
      this,
      190,
      126,
      '−',
      { name: 'chapter-remove', width: 64, height: 64, fontSize: 40, fill: 0xb23a3a },
      () => {
        this.def = { ...this.def, levels: this.def.levels.slice(0, -1) };
        this.refresh();
      },
    );
    creatorButton(
      this,
      300,
      126,
      'CLEAR',
      { name: 'chapter-clear', width: 120, height: 64, fontSize: 22, fill: 0x5c6168 },
      () => {
        this.def = { ...this.def, levels: [] };
        this.refresh();
      },
    );
    this.add
      .text(390, 84, 'CHAPTER NAME', { fontSize: '14px', color: '#9aa6ad', fontStyle: 'bold' })
      .setLetterSpacing(2);
    canvasTextInput(
      this,
      { x: 390, y: 102, w: 380, h: 50 },
      {
        maxLength: MAX_NAME,
        placeholder: 'Name your chapter',
        testId: 'chapter-name',
        value: this.name,
      },
      (v) => (this.name = v),
    );

    // ── The levels ──
    this.add
      .rectangle(
        LIST_X + LIST_W / 2,
        LIST_Y + LIST_H / 2,
        LIST_W + 16,
        LIST_H + 16,
        CREATOR_PANEL,
        1,
      )
      .setStrokeStyle(2, CREATOR_TEAL, 0.8);
    this.countText = this.add
      .text(LIST_X + LIST_W, LIST_Y - 18, '', { fontSize: '15px', color: '#9aa6ad' })
      .setOrigin(1, 0.5)
      .setName('chapter-count');

    // ── Beside them: the format selectors ──
    this.formatText = this.selector(1010, 240, 'LEVEL FORMAT', 'chapter-format', (dir) => {
      const ids = CHAPTER_FORMATS.map((f) => f.id);
      const i = ids.indexOf(this.def.format);
      this.def = { ...this.def, format: ids[(i + dir + ids.length) % ids.length] };
      this.refresh();
    });
    this.orderText = this.selector(1010, 380, 'ORDER', 'chapter-order', (dir) => {
      const ids = CHAPTER_ORDERS.map((o) => o.id);
      const i = ids.indexOf(this.def.order);
      this.def = { ...this.def, order: ids[(i + dir + ids.length) % ids.length] };
      this.refresh();
    });

    // ── Under them: the border colour ──
    this.add
      .text(LIST_X, 584, 'BORDER COLOUR', { fontSize: '14px', color: '#9aa6ad', fontStyle: 'bold' })
      .setLetterSpacing(2);
    CREATOR_BORDERS.forEach((b, i) => {
      const x = LIST_X + SWATCH / 2 + i * (SWATCH + 10);
      const y = 636;
      this.add.rectangle(x, y, SWATCH, SWATCH, b.color, 1);
      const frame = this.add
        .rectangle(x, y, SWATCH, SWATCH)
        .setStrokeStyle(2, 0x000000, 0.6)
        .setInteractive({ useHandCursor: true })
        .setName(`chapter-border-${b.id}`);
      frame.setData('id', b.id);
      frame.on('pointerup', () => {
        this.def = { ...this.def, border: b.id };
        this.refresh();
      });
      this.swatches.push(frame);
    });

    creatorButton(
      this,
      1150,
      670,
      'SAVE',
      { name: 'chapter-save', fill: CREATOR_ORANGE, width: 180, height: 60, fontSize: 28 },
      () => void this.save(),
    );
    this.refresh();
  }

  /** A ◀ value ▶ selector; returns its value text. */
  private selector(
    x: number,
    y: number,
    label: string,
    name: string,
    step: (dir: 1 | -1) => void,
  ): Phaser.GameObjects.Text {
    this.add
      .text(x, y - 46, label, { fontSize: '16px', color: '#9aa6ad', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setLetterSpacing(2);
    this.add.rectangle(x, y, 300, 56, CREATOR_PANEL, 1).setStrokeStyle(2, CREATOR_TEAL);
    const value = this.add
      .text(x, y, '', { fontSize: '19px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setName(`${name}-value`);
    for (const [dx, dir, glyph] of [
      [-185, -1, '◀'],
      [185, 1, '▶'],
    ] as const) {
      creatorButton(
        this,
        x + dx,
        y,
        glyph,
        { name: `${name}-${dir < 0 ? 'prev' : 'next'}`, width: 56, height: 56, fontSize: 24 },
        () => step(dir),
      );
    }
    return value;
  }

  private refresh(): void {
    this.formatText?.setText(CHAPTER_FORMATS.find((f) => f.id === this.def.format)?.label ?? '');
    this.orderText?.setText(CHAPTER_ORDERS.find((o) => o.id === this.def.order)?.label ?? '');
    this.countText?.setText(`${this.def.levels.length} / ${MAX_CHAPTER_LEVELS} levels`);
    for (const f of this.swatches) {
      const on = f.getData('id') === this.def.border;
      f.setStrokeStyle(on ? 5 : 2, on ? 0xffffff : 0x000000, on ? 1 : 0.6);
      f.setDepth(on ? 2 : 1);
    }
    this.drawLevels();
  }

  private drawLevels(): void {
    this.list?.destroy();
    this.children.getByName('chapter-empty')?.destroy();
    const border = findBorder(this.def.border).color;
    if (this.def.levels.length === 0) {
      this.list = undefined;
      this.add
        .text(LIST_X + LIST_W / 2, LIST_Y + LIST_H / 2, CHAPTER_EMPTY, {
          fontSize: '20px',
          color: '#9aa6ad',
          fontStyle: 'italic',
        })
        .setOrigin(0.5)
        .setName('chapter-empty');
      return;
    }
    const list = new ScrollList(this, {
      x: LIST_X,
      y: LIST_Y,
      width: LIST_W,
      height: LIST_H,
      barX: LIST_X + LIST_W + 14,
      depth: 1,
    });
    this.list = list;
    this.def.levels.forEach((code, i) => {
      const y = i * (ROW_H + 4) + ROW_H / 2;
      const name = this.namesByCode.get(code);
      list.content.add([
        this.add
          .rectangle(LIST_W / 2, y, LIST_W, ROW_H, 0x1c2130, 1)
          .setStrokeStyle(2, border, 0.9),
        this.add
          .text(14, y, chapterLevelLabel(this.def.format, this.def.order, i), {
            fontSize: '18px',
            color: '#ffd23f',
            fontStyle: 'bold',
          })
          .setOrigin(0, 0.5)
          .setName(`chapter-row-label-${i}`),
        this.add
          .text(210, y, name ?? '(not live in Explore)', {
            fontSize: '18px',
            color: name ? '#ffffff' : '#ff8a8a',
            fontStyle: name ? 'bold' : 'italic',
          })
          .setOrigin(0, 0.5)
          .setName(`chapter-row-name-${i}`),
        this.add
          .text(LIST_W - 14, y, `#${code}`, { fontSize: '15px', color: '#b9b0ff' })
          .setOrigin(1, 0.5),
      ]);
    });
    list.setContentHeight(this.def.levels.length * (ROW_H + 4));
  }

  // ── The + picker ──

  private closePicker(): void {
    this.pickerList?.destroy();
    this.pickerList = undefined;
    for (const o of this.picker.splice(0)) o.destroy();
  }

  private openPicker(): void {
    if (this.picker.length > 0) return;
    if (this.def.levels.length >= MAX_CHAPTER_LEVELS) {
      creatorFlash(this, CHAPTER_FULL);
      return;
    }
    const D = 60;
    this.picker.push(
      this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.6).setDepth(D).setInteractive(),
      this.add
        .rectangle(640, 440, 760, 500, CREATOR_PANEL, 1)
        .setStrokeStyle(3, CREATOR_TEAL)
        .setDepth(D + 1),
      this.add
        .text(640, 222, 'ADD ONE OF YOUR LEVELS', {
          fontSize: '24px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
      this.add
        .text(640, 252, 'Only levels that are live in Explore can join a chapter', {
          fontSize: '15px',
          color: '#9aa6ad',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const close = creatorButton(
      this,
      960,
      222,
      '✕',
      { name: 'chapter-picker-close', width: 44, height: 44, fill: 0xb23a3a, depth: D + 2 },
      () => this.closePicker(),
    );
    this.picker.push(close.bg, close.text);
    if (this.pickable.length === 0) {
      this.picker.push(
        this.add
          .text(640, 440, 'You have no levels yet', { fontSize: '20px', color: '#9aa6ad' })
          .setOrigin(0.5)
          .setDepth(D + 2),
      );
      return;
    }
    const W = 700;
    const list = new ScrollList(this, {
      x: 290,
      y: 280,
      width: W,
      height: 390,
      barX: 290 + W + 10,
      depth: D + 2,
    });
    this.pickerList = list;
    this.pickable.forEach((p, i) => {
      const y = i * 54 + 25;
      const added = p.code !== null && this.def.levels.includes(p.code);
      const ok = p.code !== null && !added;
      const plate = this.add
        .rectangle(W / 2, y, W, 48, ok ? 0x1c2130 : 0x101218, 1)
        .setStrokeStyle(2, ok ? CREATOR_TEAL : 0x3a3f48, 1)
        .setInteractive({ useHandCursor: ok })
        .setName(`chapter-pick-${i}`);
      const status = p.code === null ? 'Not published' : added ? 'Added' : `#${p.code}`;
      list.content.add([
        plate,
        this.add
          .text(16, y, p.name, {
            fontSize: '19px',
            color: ok ? '#ffffff' : '#5c6168',
            fontStyle: 'bold',
          })
          .setOrigin(0, 0.5),
        this.add
          .text(W - 16, y, status, { fontSize: '15px', color: ok ? '#b9b0ff' : '#5c6168' })
          .setOrigin(1, 0.5),
      ]);
      plate.on('pointerup', (ptr: Phaser.Input.Pointer) => {
        if (list.dragged || !list.inView(ptr)) return;
        if (p.code === null) {
          creatorFlash(this, 'Publish that level first — it must be live in Explore');
          return;
        }
        if (added) return;
        this.def = addChapterLevel(this.def, p.code);
        this.closePicker();
        this.refresh();
      });
    });
    list.setContentHeight(this.pickable.length * 54);
  }

  private async save(): Promise<void> {
    if (this.saving) return;
    const name = this.name.trim();
    if (!name) {
      creatorFlash(this, CHAPTER_NAME_EMPTY, 360, '#ff8a8a');
      return;
    }
    const client = accountClient();
    if (!client) return;
    this.saving = true;
    const r = await saveChapter(client, this.editId ?? newProjectId(), name, this.def);
    if (!this.sys.isActive()) return;
    if (!r.ok) {
      this.saving = false;
      creatorFlash(this, r.error, 360, '#ff8a8a');
      return;
    }
    this.scene.start('CreatorMenuScene', { tab: 'chapters' });
  }
}
